import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { EmptyState, StatusBadge } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export function BgvTab({ bgvList, onRefresh }) {
  const updateBgvCheck = async (bgvId, check, status) => {
    try {
      await api.post(`/bgv/${bgvId}/checks/${check}/update`, { status });
      toast.success(`${check} marked ${status}`);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const completeBgv = async (bgvId, result) => {
    try {
      await api.post(`/bgv/${bgvId}/complete`, { result });
      toast.success(`BGV completed: ${result}`);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <TabsContent value="bgv">
      {bgvList.length === 0 ? (
        <EmptyState title="No verifications initiated" description="Initiate BGV from a candidate card in the ATS pipeline (selected/offer stage)." testId="bgv-empty" />
      ) : (
        <div className="space-y-4" data-testid="bgv-list">
          {bgvList.map((b) => (
            <div key={b.id} className="bg-white border border-border/60 rounded-xl p-5" data-testid={`bgv-record-${b.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-semibold text-sm text-[#0B1B3D]">{b.candidate_name} — {b.job_title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Consent: {b.consent ? `given ${fmtDate(b.consent_at)}` : "awaiting candidate"} · {fmtDate(b.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={b.status} />
                  {b.result && (
                    <span className={`text-xs font-semibold rounded-full px-3 py-1 ${b.result === "clear" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`bgv-result-${b.id}`}>
                      {b.result === "clear" ? "Clear" : "Adverse"}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {b.checks.map((c) => (
                  <div key={c.name} className="border border-border/60 rounded-lg px-3 py-2 text-xs">
                    <p className="font-semibold capitalize mb-1.5">{c.name}</p>
                    {b.status === "in_verification" ? (
                      <div className="flex gap-1">
                        {["verified", "query", "failed"].map((s) => (
                          <button
                            key={s}
                            onClick={() => updateBgvCheck(b.id, c.name, s)}
                            data-testid={`bgv-${b.id}-${c.name}-${s}`}
                            className={`rounded-full px-2 py-0.5 border capitalize ${c.status === s ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "hover:border-[#2563EB]"}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="capitalize text-muted-foreground">{c.status}</span>
                    )}
                  </div>
                ))}
              </div>
              {b.status === "in_verification" && (
                <div className="flex gap-2 mt-4">
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => completeBgv(b.id, "clear")} data-testid={`bgv-clear-${b.id}`}>Complete — Clear</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => completeBgv(b.id, "adverse")} data-testid={`bgv-adverse-${b.id}`}>Complete — Adverse</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </TabsContent>
  );
}
